import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PieController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PieController,
  Title,
  Tooltip,
  Legend
);

const PizzaTipCalculator = () => {
  const [pizzaData, setPizzaData] = useState({
    totalCost: '',
    tipPercentage: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPizzaData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateTip = () => {
    const { totalCost, tipPercentage } = pizzaData;

    const totalCostNum = parseFloat(totalCost) || 0;
    const tipPercentageNum = parseFloat(tipPercentage) || 0;

    if (totalCostNum === 0 || tipPercentageNum === 0) {
      setError('Please enter valid total cost and tip percentage');
      return;
    }

    const tipAmount = (totalCostNum * tipPercentageNum) / 100;
    const totalWithTip = totalCostNum + tipAmount;

    const newResults = {
      tipAmount,
      totalWithTip
    };

    setResults(newResults);

    setChartData({
      labels: ['Total Cost', 'Tip Amount'],
      datasets: [
        {
          label: 'Cost Breakdown',
          data: [totalCostNum, tipAmount],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(255, 99, 132, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    totalCost: 20.00,
    tipPercentage: 15,
    tipAmount: 3.00,
    totalWithTip: 23.00
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Pizza Tip Calculator | Calculate Your Tip</title>
        <meta name="description" content="Calculate the appropriate tip for your pizza delivery with our Pizza Tip Calculator." />
        <meta name="keywords" content="Pizza Tip Calculator, tip calculator, pizza delivery, tip percentage" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">🍕</div>
            <h1 className="text-3xl font-bold text-gray-800">Pizza Tip Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Pizza Tip Calculator helps you determine the appropriate tip amount for your pizza delivery. Enter the total cost and desired tip percentage to see the tip amount and total cost with tip.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost:</label>
                <input
                  type="number"
                  id="totalCost"
                  name="totalCost"
                  value={pizzaData.totalCost}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter the total cost of the pizza"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tip Percentage:</label>
                <input
                  type="number"
                  id="tipPercentage"
                  name="tipPercentage"
                  value={pizzaData.tipPercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter the tip percentage"
                />
              </div>
            </div>

            <button
              onClick={calculateTip}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate Tip
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Tip Calculation Results</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Cost</h3>
                    <p className="text-gray-600">Total Cost: ${pizzaData.totalCost}</p>
                    <p className="text-gray-600">Tip Percentage: {pizzaData.tipPercentage}%</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Tip Details</h3>
                    <p className="text-gray-600">Tip Amount: ${results.tipAmount.toFixed(2)}</p>
                    <p className="text-gray-600">Total with Tip: ${results.totalWithTip.toFixed(2)}</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Pie
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Cost Breakdown',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-6">
                Here's an example calculation using sample data:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Sample Pizza Data:</h4>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Total Cost: ${exampleData.totalCost}</li>
                  <li>Tip Percentage: {exampleData.tipPercentage}%</li>
                </ul>
                
                <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
                <ol className="list-decimal pl-8 space-y-4">
                  <li>
                    <p>Tip Amount Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Tip Amount = Total Cost * (Tip Percentage / 100) = ${exampleData.totalCost} * ({exampleData.tipPercentage} / 100) = ${exampleData.tipAmount}</li>
                    </ul>
                  </li>
                  <li>
                    <p>Total with Tip Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Total with Tip = Total Cost + Tip Amount = ${exampleData.totalCost} + ${exampleData.tipAmount} = ${exampleData.totalWithTip}</li>
                    </ul>
                  </li>
                </ol>
                
                <p className="mt-6 font-semibold">Interpretation:</p>
                <p>In this example, the tip amount for a ${exampleData.totalCost} pizza with a {exampleData.tipPercentage}% tip is ${exampleData.tipAmount}, making the total cost ${exampleData.totalWithTip}.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Pizza Tip <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>A pizza tip is a gratuity given to the delivery person for their service. This calculator helps you determine the appropriate tip amount by:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Calculating the tip amount based on the total cost and desired tip percentage</li>
                <li>Showing the total cost including the tip</li>
              </ul>
              <p className="italic mt-2">Note: Tipping is a kind gesture that shows appreciation for good service.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Pizza Tip Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the Pizza Tip Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Your Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the total cost of the pizza</li>
                  <li>Enter the desired tip percentage</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate Tip</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate Tip" to generate the results</li>
                  <li>View the tip amount and total cost with tip</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Interpret Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Analyze the tip amount and total cost with tip</li>
                  <li>Use the chart to visualize the cost breakdown</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Consider the quality of service when deciding the tip percentage</li>
                  <li>Ensure accurate input for the total cost and tip percentage</li>
                  <li>Use this tool to make tipping easy and fair</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PizzaTipCalculator;